import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '@/components/icons';
import { Modal } from '@/components/ui/Modal';
import { C1Grid } from '@/components/c1';
import { EduCorePaths, type EduCorePath } from '@/lib/educoreLinks';

type DeskAction = {
  id: string;
  path: EduCorePath;
  icon: IconName;
};

/**
 * Student shortcuts into EduCore. Labels live in i18n (`home.studyDesk.actions.*`).
 * Form B reuses course registration — that is where the printable Form B lives.
 */
export const STUDENT_STUDY_DESK_ACTIONS: readonly DeskAction[] = [
  { id: 'academicStructure', path: EduCorePaths.studentAcademicStructure, icon: 'academic' },
  { id: 'courseRegistration', path: EduCorePaths.studentCourseRegistration, icon: 'checklist' },
  { id: 'formB', path: EduCorePaths.studentFormB, icon: 'report' },
  { id: 'caResults', path: EduCorePaths.studentCaResults, icon: 'tasks' },
  { id: 'examResults', path: EduCorePaths.studentExamResults, icon: 'sparkles' },
  { id: 'transcript', path: EduCorePaths.studentProgress, icon: 'history' },
  { id: 'holidays', path: EduCorePaths.events, icon: 'announcement' },
  { id: 'payFees', path: EduCorePaths.studentFees, icon: 'products' },
] as const;

type StudentStudyDeskProps = {
  open: boolean;
  onClose: () => void;
  /** Build an absolute EduCore URL, or null when launch is not configured. */
  hrefFor: (path: EduCorePath) => string | null;
  /** Fallback when EduCore launch URL is missing. */
  onFallback: () => void;
};

export function StudentStudyDesk({ open, onClose, hrefFor, onFallback }: StudentStudyDeskProps) {
  const { t } = useTranslation();

  const launch = (path: EduCorePath) => {
    const href = hrefFor(path);
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
      onClose();
      return;
    }
    onFallback();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('home.studyDesk.title')}
      description={t('home.studyDesk.description')}
      bodyClassName="study-desk-body"
    >
      <C1Grid min={200} role="group" aria-label={t('home.studyDesk.title')}>
        {STUDENT_STUDY_DESK_ACTIONS.map((action) => (
          <button
            key={action.id}
            type="button"
            className="c1-actionCard"
            onClick={() => launch(action.path)}
            aria-label={t(`home.studyDesk.actions.${action.id}`)}
          >
            <span className="c1-actionCardIcon" aria-hidden="true">
              <Icon name={action.icon} size={20} />
            </span>
            <span className="c1-actionCardText">
              <span className="c1-actionCardLabel">{t(`home.studyDesk.actions.${action.id}`)}</span>
              <span className="c1-actionCardDescription">
                {t(`home.studyDesk.hints.${action.id}`)}
              </span>
            </span>
            <span className="c1-actionCardIcon" aria-hidden="true">
              <Icon name="external" size={16} />
            </span>
          </button>
        ))}
      </C1Grid>
    </Modal>
  );
}
